// Client portal Warehouse Activity tab — read-only feed of Slack-sourced
// activities (returns, goods-in, goods-out, time, packages). The 'fee'
// column shows what was billed for each entry.

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { getMeWarehouseActivities } from '../../../lib/api'
import {
  formatCurrency, formatDate, formatInt,
  currentMonthStart, todayStr, DateRangeFilter,
} from './helpers'

const TYPE_LABELS = {
  goods_in:   'Goods In',
  package_in: 'Package In',
  return:     'Return',
  goods_out:  'Goods Out',
  time:       'Time',
  container_20ft: '20ft Container',
  container_40ft: '40ft Container',
  purchase:   'Purchase',
}

const TYPE_COLOURS = {
  goods_in:   'bg-emerald-100 text-emerald-700',
  package_in: 'bg-blue-100 text-blue-700',
  return:     'bg-amber-100 text-amber-700',
  goods_out:  'bg-violet-100 text-violet-700',
  time:       'bg-slate-200 text-slate-700',
  container_20ft: 'bg-teal-100 text-teal-700',
  container_40ft: 'bg-cyan-100 text-cyan-800',
  purchase:   'bg-indigo-100 text-indigo-700',
}

export default function ActivityTab({ clientId }) {
  const [from, setFrom] = useState(currentMonthStart())
  const [to,   setTo]   = useState(todayStr())

  const params = useMemo(() => ({
    ...(from && { from }),
    ...(to   && { to   }),
  }), [from, to])

  const { data: acts = [], isLoading, isError, error } = useQuery({
    queryKey: ['me', 'activities', clientId, params],
    queryFn:  () => getMeWarehouseActivities(clientId, params),
    enabled:  !!clientId,
  })

  const totalFee = acts.reduce((s, a) => s + (Number(a.fee) || 0), 0)

  return (
    <div className="space-y-4">
      <div className="card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Warehouse activity</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Activity recorded against your account — returns, goods-in, time, etc.
            </p>
          </div>
          <DateRangeFilter from={from} to={to} onChange={({ from: f, to: t }) => { setFrom(f); setTo(t) }} />
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 gap-2 text-slate-400">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        ) : isError ? (
          // Surface the failure inline — an empty table under a healthy
          // header otherwise reads as "quiet month" rather than a broken
          // fetch, and hides real backend problems from the client.
          <div className="p-4 text-sm text-red-700 bg-red-50 border-t border-red-200 flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            <span>Failed to load: {error?.response?.data?.detail || error?.message || 'Unknown error'}</span>
          </div>
        ) : acts.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400">No activity in this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-2.5">Date</th>
                  <th className="px-4 py-2.5">Type</th>
                  <th className="px-4 py-2.5 text-right">Qty</th>
                  <th className="px-4 py-2.5">Reference</th>
                  <th className="px-4 py-2.5">Notes</th>
                  <th className="px-4 py-2.5 text-right">Fee</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {acts.map((a, i) => (
                  <tr key={a.id || i} className={i % 2 === 1 ? 'bg-slate-50/40' : ''}>
                    <td className="px-4 py-2 text-slate-600">{formatDate(a.activity_date)}</td>
                    <td className="px-4 py-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${TYPE_COLOURS[a.activity_type] || 'bg-slate-100 text-slate-600'}`}>
                        {TYPE_LABELS[a.activity_type] || a.activity_type}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-700">{formatInt(a.quantity)}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-700">{a.reference || '—'}</td>
                    <td className="px-4 py-2 text-slate-500 max-w-md truncate" title={a.notes || ''}>{a.notes || '—'}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-700">{formatCurrency(a.fee)}</td>
                  </tr>
                ))}
                <tr className="bg-slate-50 border-t-2 border-slate-200">
                  <td colSpan={5} className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Total ({acts.length} entr{acts.length === 1 ? 'y' : 'ies'})
                  </td>
                  <td className="px-4 py-2.5 text-right font-bold text-slate-800 tabular-nums">{formatCurrency(totalFee)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
